<template>
    <layout>
        <div class="p-4">
            <template v-if="defaultState == 'basic'">
                <div class="flex w-full">
                    <div class="flex w-full lg:w-2/3">
                        <form-basic-search
                            :processing="processing"
                            :search_form="search_form"
                            @formSubmit="handleFind"
                        />
                    </div>
                </div>
                <div class="pt-2 font-semibold" v-if="claims_search_criteria" data-cy="previous-search-criteria">
                    {{ $t('claims.search_form.search_criteria') }}
                    <template v-for="(value, name, index) in claims_search_criteria" :key="index">
                        <span>
                            {{ name }}:
                            <span class="text-default-purple">{{ value }}</span>
                        </span>
                    </template>
                </div>
                <div class="border-t w-full absolute left-0 mt-2"></div>
            </template>
            <template v-if="defaultState == 'advance'">
                <div class="rounded-md shadow-md">
                    <form-advance-search
                        :processing="processing"
                        :search_form="search_form"
                        :form_mode="form_mode"
                        @formSubmit="handleFind"
                        @clear="handleClear"
                        @cancel="handleCancel"
                    />
                    <template v-if="claims_search_criteria">
                        <div class="flex">
                            <div
                                class="px-6 pt-2 pb-4 font-semibold flex w-3/4 md:w-5/6 lg:w-10/12 xl:w-11/12"
                                data-cy="previous-search-criteria"
                            >
                                {{ $t('claims.search_form.search_criteria') }}
                                <template v-for="(value, name, index) in claims_search_criteria" :key="index">
                                    <span>
                                        <span class="pl-1">{{ name }}:</span>
                                        <span class="text-default-purple">{{ value }}</span>
                                    </span>
                                </template>
                            </div>
                            <div class="flex w-1/4 md:w-1/6 lg:w-2/12 xl:w-1/12">
                                <div class="border-l h-full"></div>
                            </div>
                        </div>
                    </template>
                </div>
            </template>
            <div class="list-table pt-2 md:pt-4 overflow-x-auto md:overflow-visible">
                <div class="py-4">
                    <span class="font-semibold block pb-1 md:inline-block md:pb-0 md:pr-4" data-cy="search-items-found">
                        {{
                            $t('claims.search_form.search_results', {
                                total: claims_search_count,
                                motor_total: claims_search_motor_count,
                                non_motor_total: claims_search_non_motor_count,
                            })
                        }}
                    </span>
                    <a
                        data-cy="button-switch"
                        @click="handleSwitch"
                        class="underline text-default-purple hover:text-highlight-purple"
                        >{{ switchButtonText }}</a
                    >
                </div>
                <claims-table
                    :search_id="search_id"
                    @fetch="handleFetchData"
                    @fetchSplitCount="handleSplitCount"
                    @clear="handleClear"
                    ref="ClaimsTable"
                />
            </div>
        </div>
    </layout>
</template>

<script>
import Layout from '@/layouts/Layout.vue';
import ClaimsTable from '@/components/tables/Claims.vue';
import FormBasicSearch from '@/components/forms/claims/BasicSearch.vue';
import FormAdvanceSearch from '@/components/forms/claims/AdvanceSearch.vue';

import store from '@/store';

export default {
    components: {
        Layout,
        ClaimsTable,
        FormBasicSearch,
        FormAdvanceSearch,
    },
    data() {
        return {
            search_id: '',
            search_form: {
                basic_search: '',
                name: '',
                national_id: '',
                reg_no: '',
                years_from: 'all',
                chassis_no: '',
                engine_no: '',
                risk_location: '',
                all_match: 1,
            },
            form_mode: 'results',
            search_values: {},
            claims_search_count: 0,
            claims_search_motor_count: 0,
            claims_search_non_motor_count: 0,
            claims_search_criteria: {},
            processing: false,
        };
    },
    mounted() {
        this.search_id = this.$route.params.id;
        this.$refs.ClaimsTable.fetch({}, this.search_id);
    },
    computed: {
        defaultState() {
            return this.$store.state.system.search_state;
        },
        switchButtonText() {
            switch (this.$store.state.system.search_state) {
                case 'basic':
                    return this.$t('claims.search_form.buttons.switch_basic');
                case 'advance':
                    return this.$t('claims.search_form.buttons.switch_advance');
            }
        },
    },
    methods: {
        handleFind() {
            this.search_values = this.search_form;
            let years_from = 0;

            if (this.search_values.years_from !== 'all') {
                years_from = parseInt(this.search_values.years_from) || 0;
            }

            switch (this.$store.state.system.search_state) {
                case 'basic':
                    if (this.search_values.basic_search == '') {
                        this.$notification.error({
                            message: this.$t('claims.notifications.error.basic_empty'),
                        });
                        return;
                    }
                    break;
                case 'advance':
                    if (
                        this.search_values.name == '' &&
                        this.search_values.national_id == '' &&
                        this.search_values.reg_no == '' &&
                        this.search_values.chassis_no == '' &&
                        this.search_values.engine_no == '' &&
                        this.search_values.risk_location == ''
                    ) {
                        this.$notification.error({
                            message: this.$t('claims.notifications.error.advance_empty'),
                        });
                        return;
                    }
                    break;
            }

            this.processSearchValues({
                ...this.search_values,
                years_from: years_from,
            });
        },
        handleClear() {
            this.processing = true;

            this.search_form = {
                basic_search: '',
                name: '',
                national_id: '',
                reg_no: '',
                years_from: 'all',
                chassis_no: '',
                engine_no: '',
                risk_location: '',
                all_match: 1,
            };

            this.processing = false;
        },
        handleCancel() {
            store.dispatch('system/updateSearchState', 'basic');

            this.handleClear();
        },
        handleFetchData(values) {
            this.claims_search_count = values.search_count;
            this.claims_search_criteria = values.search_criteria;

            this.processing = false;
        },
        handleSplitCount(values) {
            this.claims_search_motor_count = values.search_motor_count;
            this.claims_search_non_motor_count = values.search_non_motor_count;
        },
        processSearchValues(data) {
            this.processing = true;

            if (this.$store.getters['user/isMultiAssociation']) {
                data.association_id = this.$store.getters['user/currentAssociationId'];
            }

            this.$http
                .post('/api/claims/search', data)
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$router.push({
                            name: 'home-claims-search',
                            params: {
                                id: response.data.id,
                            },
                        });

                        this.search_id = response.data.id;
                        this.$refs.ClaimsTable.fetch({}, this.search_id);
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVSB01' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        handleSwitch() {
            if (this.$store.state.system.search_state == 'basic') {
                store.dispatch('system/updateSearchState', 'advance');
            } else {
                store.dispatch('system/updateSearchState', 'basic');
            }

            this.handleClear();
        },
    },
};
</script>
